import { getClosestTheme } from '@glu/theming/glu-css-override';
import { Collection, util } from '@glu/core';
import locale from '@glu/locale';
import 'jquery-ui/ui/version';
import 'jquery-ui/ui/plugin';
import 'jquery-ui/ui/widget';
import 'jquery-ui/ui/disable-selection';
import 'jquery-ui/ui/widgets/mouse';
import 'jquery-ui/ui/widgets/sortable';
import 'jquery-ui/ui/widgets/resizable';
import './localeStrings';
import TableCaptionView from './body/tableCaption';
import ColumnView from './column/column';
import comparatorFactory from './comparatorFactory';
import FilterBadgeHeaderView from './filter/badges/filterBadgesHeaderLayout';
import FilterBadgeView from './filter/badges/filterBadgesRow';
import SearchView from './filter/dynamicTypeSearch';
import ExclusiveFilterProcessor from './filter/exclusiveFilterProcessor';
import FilterProcessor from './filter/filterProcessor';
import SingularFilterProcessor from './filter/singularFilterProcessor';
import BulkActionsView from './footer/bulkActions';
import TableFooterLayout from './footer/tableFooterLayout';
import defaultOptions from './gridDefaultOptions';
import TableHeaderView from './header/tableHeader';
import TableHeaderLayout from './header/tableHeaderLayout';
import ManageColumnsView from './manageColumns/manageColumns';
import MultiColumnSortView from './multiColumnSort/multiColumnSortView';
import PagerView from './paginate/pager';
import ServerSidePagerView from './paginate/serverSidePager';
import SavedViews from './savedViews/savedViews';
import SectionLayout from './sectionLayout';
import StateModel from './state/stateModel';
import TableLayout from './tableLayout';

import './grid.styles';

function formatIdsToObject(listOfIds) {
  if (!listOfIds) {
    return;
  }

  return listOfIds.reduce((memo, id) => ({
    ...memo,
    [id]: id
  }), {});
}

export default SectionLayout.extend({
  className: 'grid',
  gridCollection: false,
  pageCollection: false,
  filterCollection: false,
  filterView: false,
  pagerView: false,
  tableView: false,
  tableBody: false,
  tableHeader: false,
  bulkActionsViewClass: BulkActionsView,
  filterViewClass: SearchView,
  tableViewClass: TableLayout,
  tableBodyClass: ColumnView,
  tableCaptionClass: TableCaptionView,
  tableHeaderClass: TableHeaderView,
  tableHeaderLayoutClass: TableHeaderLayout,
  tableFooterLayoutClass: TableFooterLayout,
  filterBadgeRowViewClass: FilterBadgeView,
  filterBadgeViewClass: FilterBadgeView,
  defaultOptions,

  initialize(...args) {
    let [options] = args;
    SectionLayout.prototype.initialize.apply(this, args);

    // Need to keep reference to options due to build process on grid render
    options = { ...options || {} };
    this.options = options;

    if (!this.options.collection && !this.options.filter) {
      throw new Error(locale.get('notProvideFilteringMsg'));
    }

    util.defaults(this.options, this.defaultOptions);

    this.masterCollection = this.options.collection;
    this.gridCollection = this.options.collection;

    if (this.options.trackState) {
      this.stateModel = this.createStateModel(this.options.trackState, this.options.state);
    } else {
      this.stateModel = null;
    }

    this.captureState = this.options.captureState;

    // Set the legacy pagination options based on `pageable`.
    if (this.options.pageable) {
      this.options.paginateMethod = this.options.pageable.method;
      this.options.serverSidePagination = !!this.options.pageable.method;
      this.options.clientSidePagination = (this.options.pageable === true) || !this.options.pageable.server;
      this.options.paginate = this.options.pageable.type || 'advanced';
      this.options.pageSize = this.options.pageable.pageSize || 50;
      this.options.pageSizeValues = this.options.pageable.pageSizeValues || [5, 10, 25, 50, 100, 250];
    }

    this.pagerViewClass = options.serverSidePagination ?
      ServerSidePagerView :
      PagerView;

    // pager defaults to the footer, can be set on subclass or in options with options taking precedence
    this.pagerLocation = this.options.pagerLocation || this.pagerLocation || 'footer';

    this.disableDynamicColumnWidths = this.options.disableDynamicColumnWidths;

    this.hasStickyFooter = this.pagerLocation === 'footer' && this.options.stickyFooter;

    // if savedViews functionality are enabled than we should initialize stateModel.
    if (!util.isEmpty(this.options.savedViews)) {
      this.options.trackState = true;
      this.stateModel = this.createStateModel(this.options.trackState, this.options.state);
      // eslint-disable-next-line new-cap
      this.savedViewsCollection = new this.options.savedViewsCollection(null, {
        gridName: this.options.savedViews.gridName,
        stateModel: this.stateModel,
        storage: this.options.savedViews.storage
      });
    }

    // we need this to keep initial options (default sorting, pageSize, etc.)
    this.initialOptions = util.extend({}, this.options);
    this.setDefaultHiddenColumns();
  },

  templateHelpers() {
    return {
      disableDynamicColumnWidths: this.disableDynamicColumnWidths,
      showLoaderOnFilter: this.options.showLoaderOnFilter
    };
  },

  onClose() {
    // should close all table views
    if (this.tableCaption) {
      this.tableCaption.close();
    }
    if (this.tableHeader) {
      this.tableHeader.close();
    }
    if (this.tableBody) {
      this.tableBody.close();
    }

    if (this.filterView) {
      this.filterView.close();
    }

    if (this.filterProc) {
      this.filterProc.close();
    }
  },

  onBeforeRender() {
    this.build();
  },

  onShow() {
    const oldTheme = this.options.theme;
    this.options.theme = this.options.theme || getClosestTheme(this.$el);

    if (oldTheme !== this.options.theme) {
      this.render();
    }
  },

  build() {
    if (this.options.enableStickySelectedRows || (this.options.preSelectedRowIds && this.options.rowIdColumn)) {
      this.enableStickySelectedRows(this.options);
    }

    this.triggerMethod('before:build', this);

    if (!this.usedSelectedView) {
      this.loadDefaultSavedView();
    }

    if (this.options.filterable || this.options.filter) {
      this.buildFilterView();
    }

    if (this.options.paginate) {
      this.buildPagerView();
    }

    if (this.options.filterable || (this.options.filter && this.options.badge !== false)) {
      this.buildFilterBadgeView();
      this.buildFilterBadgeRowView();
      this.buildFilterBadgeHeaderView();
    }

    this.buildHeaderLayout();
    this.buildManageColumnsLayout();
    this.buildSavedViewsLayout();
    this.buildFooterLayout();
    this.buildTableView();
    this.addListeners();
    this.triggerMethod('build', this);
    this.postBuild();
    this.triggerMethod('after:build', this);

    if (this.options.preSelectedRowIds && this.options.rowIdColumn) {
      this.processPreSelections(this.options.preSelectedRowIds, this.options.rowIdColumn);
    }
  },

  /**
   * The header layout can be specified by the consumer during init.  The standard layout supports filtering,
   * advanced search, etc.  The small screen version supports single textbox searching, etc.
   *
   */
  buildHeaderLayout() {
    const TableHeaderLayoutClass = this.tableHeaderLayoutClass;

    this.headerLayout = new TableHeaderLayoutClass();

    if (this.pagerLocation === 'header' && this.pagerView) {
      this.headerLayout.setPager(this.pagerView);
    }
  },

  buildManageColumnsLayout() {
    if (this.options.disableColumnManagement) {
      return false;
    }

    const columnsLength = this.columns.size();
    const disableRemoveLength = this.columns.filter((column) => column.get('disableRemove')).length || 0;

    if (columnsLength - disableRemoveLength <= 1) {
      return false;
    }

    this.manageColumnsLayout = new ManageColumnsView({
      columns: this.columns,
      grid: this
    });
  },

  buildSavedViewsLayout() {
    if (util.isEmpty(this.options.savedViews) || !this.savedViewsCollection) {
      return;
    }

    this.savedViews = new SavedViews({
      grid: this,
      collection: this.savedViewsCollection,
      stateModel: this.stateModel
    });
  },

  buildFooterLayout() {
    const TableFooterLayoutClass = this.tableFooterLayoutClass;
    const BulkActionsViewClass = this.bulkActionsViewClass;

    this.footerLayout = new TableFooterLayoutClass({
      hasStickyFooter: this.hasStickyFooter,
      grid: this
    });

    if (this.pagerLocation === 'footer' && this.pagerView) {
      this.footerLayout.setPager(this.pagerView);
    }

    this.footerLayout.setBulkActions(new BulkActionsViewClass({
      bulkActions: this.options.bulkActions,
      columns: this.columns,
      collection: this.gridCollection,
      getSelectedRows: this.getSelectedRows.bind(this),
      getSelectedRowModels: this.getSelectedRowModels.bind(this),
      gridCid: this.cid,
      id: this.options.id
    }));
  },

  buildFilterView() {
    const opts = this.options;
    // filteredCollection can be used later as an input to another process in the pipeline, paging for example
    // gridCollection is the collection used to show the visible grid rows and will be the same unless
    // there is paging or another downstream process that changes it.
    this.filteredCollection = new Collection(opts.collection.models);
    this.gridCollection = this.filteredCollection;

    // Support both legacy `filterMethod` and `filterable.method` solution
    if (!opts.filterMethod) {
      opts.filterMethod = opts.filterable && opts.filterable.method ? util.bind(opts.filterable.method, this.masterCollection) : undefined;
    }

    if (opts.filter && opts.badge === false) {
      this.filterProc = new SingularFilterProcessor({
        inputCollection: this.masterCollection,
        outputCollection: this.filteredCollection,
        filterMethod: opts.filterMethod,
        stateModel: this.stateModel,
        columns: this.columns
      });
    } else if (opts.filter === 'multi' && opts.badge) {
      this.filterProc = new FilterProcessor({
        inputCollection: this.masterCollection,
        outputCollection: this.filteredCollection,
        filterMethod: opts.filterMethod,
        stateModel: this.stateModel,
        columns: this.columns
      });
    } else {
      this.filterProc = new ExclusiveFilterProcessor({
        inputCollection: this.masterCollection,
        outputCollection: this.filteredCollection,
        filterMethod: opts.filterMethod,
        stateModel: this.stateModel,
        columns: this.columns
      });
    }

    const FilterViewClass = this.filterViewClass;

    this.filterView = new FilterViewClass({
      collection: this.columns,
      filterViewClass: this.filterViewClass,
      filterProc: this.filterProc,
      filtersCollection: this.filterProc.filters,
      masterCollection: this.masterCollection,
      gridSearch: opts.gridSearch,
      disableAuxControl: !opts.disableAuxControl,
      grid: this
    });
  },

  disableFilterView() {
    this.filterView.disable();
  },

  enableFilterView() {
    this.filterView.enable();
  },

  toggleFilterView() {
    this.filterView.toggle();
  },

  buildFilterBadgeView() {
    this.filterBadgeView = new FilterBadgeView({
      columns: this.columns,
      filters: this.filterProc.filters
    });
  },

  buildFilterBadgeRowView() {
    if (!this.options.columnBadges) {
      return;
    }

    this.FilterBadgeView = new FilterBadgeView({
      columns: this.columns,
      filters: this.filterProc.filters
    });
  },

  buildFilterBadgeHeaderView() {
    this.filterBadgeHeaderView = new FilterBadgeHeaderView({
      columns: this.columns,
      masterCollection: this.masterCollection,
      filters: this.filterProc.filters,
      filterProc: this.filterProc
    });
  },

  buildPagerView() {
    const opts = this.options;

    // if we have a filter then we need to have the pager page the output of the filters and render that
    // otherwise the filters will never apply, this also improves sorting performance (see buildTableView() below)
    let pagerInputCollection = this.filteredCollection || this.masterCollection;

    const PagerViewClass = this.pagerViewClass;

    // bypass the 11.22 release changes if server side paging is active
    if (opts.serverSidePagination) {
      pagerInputCollection = this.masterCollection;
    }

    this.gridCollection = new Collection();

    this.pagerView = new PagerViewClass({
      inputCollection: pagerInputCollection,
      outputCollection: this.gridCollection,
      type: opts.paginate,
      pageSize: opts.pageSize,
      currentPage: opts.currentPage,
      pageSizeValues: opts.pageSizeValues,
      paginateMethod: opts.paginateMethod,
      stateModel: this.stateModel,
      resultsName: opts.resultsName,
      id: this.options.id
    });

    this.pagerView.slice();
  },

  buildTableView() {
    let opts = this.options;

    // Set default values as required
    opts = util.defaults(opts, {
      sortable: true,
      filterable: true,
      resizable: true,
      draggable: true
    });

    // Support legacy options
    if (opts.sorting !== undefined) {
      opts.sortable = opts.sorting;
    }

    if (opts.filtering !== undefined) {
      opts.filterable = opts.filtering;
    }

    if (opts.resizing !== undefined) {
      opts.resizable = opts.resizing;
    }

    if (opts.reordering !== undefined) {
      opts.draggable = opts.reordering;
    }

    const TableView = this.tableViewClass;
    const TableCaption = this.tableCaptionClass;
    const TableHeader = this.tableHeaderClass;
    const TableBody = this.tableBodyClass;

    // default to sorting the output of the filter, if it exists
    // this can vastly improve sorting operations as:
    // we don't have to sort the entire master collection, just the filtered version which is smaller if filtering
    // we don't have to filter on sort of the master collection
    // by not sorting data we are going to toss away during filtering anyhow
    // Pipeline is thus: master --> filter --> sort --> page
    let sortableCollection = this.filteredCollection;

    this.tableView = new TableView({
      masterCollection: this.masterCollection,
      emptyViewText: opts.emptyViewText,
      loadingView: opts.loadingView,
      loadingViewText: opts.loadingViewText,
      disableDynamicColumnWidths: opts.disableDynamicColumnWidths,
      serverSidePagination: opts.serverSidePagination,
      grid: this
    });

    this.tableCaption = new TableCaption({
      text: opts.caption
    });

    // if we don't have filtering enabled then the sortable collection will be the master collection
    // unless we have server side paging
    if (!sortableCollection) {
      if (opts.paginate && !opts.serverSidePagination) {
        sortableCollection = this.masterCollection;
      } else {
        sortableCollection = this.gridCollection;
      }
    }

    // Support both legacy `sortMethod` and `sortable.method` solution
    if (!opts.sortMethod) {
      opts.sortMethod = opts.sortable && opts.sortable.method ? util.bind(opts.sortable.method, this.masterCollection) : undefined;
    }

    this.tableHeader = new TableHeader({
      gridCid: this.cid,
      collection: opts.columns,
      masterCollection: this.masterCollection,
      sortMethod: opts.sortMethod,
      sortableCollection,
      clientSidePagination: opts.clientSidePagination || (opts.paginate && !opts.serverSidePagination),
      layout: this,
      sortable: !!opts.sortable,
      filterable: !!opts.filterable,
      resizable: !!opts.resizable,
      draggable: !!opts.draggable,
      defaultColumnWidth: opts.defaultColumnWidth,
      defaultSort: opts.defaultSort,
      disableDynamicColumnWidths: opts.disableDynamicColumnWidths,
      stateModel: this.stateModel,
      grid: this,
      enableColumnRename: opts.enableColumnRename,
      disableColumnManagement: opts.disableColumnManagement
    });

    this.tableBody = new TableBody({
      gridCid: this.cid,
      grid: this,
      collection: this.gridCollection,
      columns: opts.columns,
      rowClassName: opts.rowClassName,
      groupBy: opts.groupBy,
      editOptions: opts.editOptions,
      rowSubView: opts.rowSubView,
      rowSubViewOptions: opts.rowSubViewOptions,
      disableDynamicColumnWidths: opts.disableDynamicColumnWidths,
      theme: opts.theme
    });
  },

  onTableViewClientSidePaginationSort() {
    this.pagerView.handleFirstPageClick();
  },

  changeCurrentPage(pageNumber) {
    if (this.pagerView.currentPage !== pageNumber) {
      this.pagerView.handlePageSelect(pageNumber);
    }
  },

  buildMultiColumnSortView() {
    let { multiColumnSort } = this.options;
    let multiColumnSortState = false;

    if (this.stateModel && this.stateModel.has('multiColumnSort')) {
      multiColumnSortState = this.stateModel.get('multiColumnSort');
    }

    if (this.options.multiColumnSort === true) {
      multiColumnSort = {};
    }

    const multiColumnSortViewOptions = util.extend({
      columnCollection: this.columns,
      grid: this,
      multiColumnSortState
    }, multiColumnSort);

    this.multiColumnSortView = new MultiColumnSortView(multiColumnSortViewOptions);

    return this.multiColumnSortView;
  },

  applyMultiColumnSort(sortingCollection) {
    // Clear current one column sort
    this.columns.each((model) => {
      model.set({
        sorted: false,
        sort: false
      });
    });

    const multiColumnSortCollection = this.filteredCollection.clone();
    const comparator = comparatorFactory.multiColumnComparator(sortingCollection);

    multiColumnSortCollection.comparator = comparator;

    multiColumnSortCollection.sort({
      comparator
    });

    this.filteredCollection.comparator = null;

    this.filteredCollection.set(multiColumnSortCollection.toJSON());

    if (this.filterProc.filters.length) {
      this.listenToOnce(this.filterProc.filters, 'remove', () => {
        this.applyMultiColumnSort(sortingCollection);
      }, this);
    }

    if (this.stateModel) {
      this.stateModel.setMultiColumnSort(sortingCollection.toJSON().map((column) => column.field), true);
    }
  },

  unApplyMultiColumnSort() {
    if (this.options.defaultSort) {
      this.tableHeader.initDefaultSort(this.options);
    } else {
      this.filteredCollection.set(this.options.collection.toJSON());
      this.filterProc.process();
    }

    if (this.stateModel) {
      this.stateModel.setMultiColumnSort(null, false);
    }

    this.stopListening(this.filterProc.filters, 'remove');
  },

  postBuild() {
    if (this.filterBadgeView) {
      this.headerLayout.setFilterBadges(this.filterBadgeView);
    }

    if (this.filterView) {
      this.headerLayout.setFilters(this.filterView);
    }

    if (this.savedViews) {
      this.headerLayout.setSavedViews(this.savedViews);
    }

    if (this.options.multiColumnSort) {
      this.headerLayout.setMultiColumnSort(this.buildMultiColumnSortView());
    }

    if (this.FilterBadgeView) {
      this.tableBody.setFirstView(this.FilterBadgeView);
    } else if (this.options.bodyFirstView) {
      this.tableBody.setFirstView(this.options.bodyFirstView);
    }

    if (this.filterBadgeHeaderView) {
      this.headerLayout.setFilterBadgeHeader(this.filterBadgeHeaderView);
    }

    this.tableView.setCaption(this.tableCaption);

    this.tableView.setHeader(this.tableHeader);

    this.tableView.setBody(this.tableBody);

    this.setHeader(this.headerLayout);

    this.setFooter(this.footerLayout);

    this.setContent(this.tableView);

    this.setManageColumns(this.manageColumnsLayout);
  },

  // Return an array of all selected model items in the grid collection.
  // Note: there may be a more appropriate home for this function.  For now it is most convenient to
  // expose this at the grid layout level.
  getSelectedRows() {
    const { enableStickySelectedRows, preSelectedRowIds, rowIdColumn } = this.options;

    if (enableStickySelectedRows || (preSelectedRowIds && rowIdColumn)) {
      return Array.isArray(this.selectedRowModels.toJSON()) ? this.selectedRowModels.map(model => model.cid) : [];
    }

    const selectedRows = [];
    const { models } = this.masterCollection;

    for (const i in models) {
      if (models[i].hasOwnProperty('gridRowSelected') && models[i].gridRowSelected) {
        selectedRows.push({
          cid: models[i].cid
        });
      }
    }

    return selectedRows;
  },

  getSelectedRowModels() {
    if (this.options.enableStickySelectedRows || (this.options.preSelectedRowIds && this.options.rowIdColumn)) {
      // shallow clone the array
      return this.selectedRowModels ? this.selectedRowModels.models.concat([]) : [];
    }

    return util.map(this.getSelectedRows(), row => this.masterCollection.get(row.cid));
  },

  handleFilter(filter) {
    this.filterProc.addFilter(filter);
  },

  handleDelete(view) {
    this.masterCollection.remove(view.model);
  },

  setPageSize(pageSize) {
    if (!pageSize) {
      return;
    }

    if (!util.isNullOrUndefined(this.options.pageable)) {
      util.extend(this.options.pageable, {
        pageSize
      });
    }

    util.extend(this.options, {
      pageSize
    });
  },

  setCurrentPage(currentPage) {
    if (!currentPage) {
      return;
    }

    util.extend(this.options, {
      currentPage
    });
  },

  setSort(sort) {
    if (util.isEmpty(sort)) {
      this.options.defaultSort = this.initialOptions.defaultSort || {};
      return false;
    }

    const field = Object.keys(sort)[0];

    this.options.defaultSort = {
      field,
      order: sort[field]
    };
  },
  /**
   * Sorts column collection in order defined by columnOrder and
   * appends other unsorted columns(that was not found in columnOrder array) to the end
   */
  setColumnOrder(columnOrder) {
    if (!columnOrder || !columnOrder.length) {
      this.options.columns = util.isArray(this.initialOptions.columns) ? this.initialOptions.columns : this.initialOptions.columns.toJSON();
      return false;
    }

    const sorted = new Array(columnOrder.length);
    const unsorted = [];

    util.each(this.options.columns, (column) => {
      const idx = columnOrder.indexOf(column.field);
      // if found puts by same index defined in columnOrder
      if (idx !== -1) {
        sorted[idx] = column;
      } else {
        unsorted.push(column);
      }
    });

    // the final result is all the columns that have field ids contained in columnOrder sorted
    // in that order followed by all the remaining columns.
    // We then filter out any holes in the array to account for holes in the columnOrder
    // or the columnOrder possibly referring to columns that don't exist anymore
    this.options.columns = sorted.concat(unsorted).filter(Boolean);
  },

  setHiddenColumns(hiddenColumns) {
    if (!hiddenColumns) {
      return;
    }

    util.each(this.options.columns, (column) => {
      if (typeof column.condition !== 'function') {
        column.condition = (hiddenColumns.indexOf(column.field) === -1);
      } else if (!hiddenColumns.length) {
        column.condition = true;
      }
    });
  },

  setColumnWidths(columnsWidth) {
    columnsWidth = columnsWidth || {};

    const initialColumns = util.isArray(this.initialOptions.columns) ? this.initialOptions.columns : this.initialOptions.columns.toJSON();

    // this need to restore initial values if width wasn't changed
    if (util.isEmpty(columnsWidth)) {
      util.each(initialColumns, (column) => {
        const { field } = column;
        const columnWidth = column.width || 'auto';

        columnsWidth[field] = columnWidth;
      });
    }

    util.each(this.options.columns, (column) => {
      const { field } = column;
      const columnWidth = column.width || 'auto';

      column.width = columnsWidth[field] || columnWidth;
    });
  },

  setFiltersBeforeBuild(filters) {
    this.listenTo(this, 'build', () => {
      util.each(filters, (value, prop) => {
        this.options.collection.trigger(`grid:filter:${prop}`, value);
      });

      this.trigger('state:filters:loaded');
    });
  },

  convertStateToObject(array) {
    return {
      pageSize: array[0],
      currentPage: array[1],
      sort: array[2],
      columnOrder: array[3],
      hiddenColumns: array[4],
      filters: array[5],
      multiColumnSort: array[6]
    };
  },

  loadStateObject() {
    let state = this.options.state || {};

    if (util.isEmpty(state)) {
      return this.initColumns();
    }

    if (typeof this.options.columns.toJSON === 'function') {
      this.options.columns = this.options.columns.toJSON();
    }

    if (Array.isArray(state)) {
      state = this.convertStateToObject(state);
    }

    this.setPageSize(state.pageSize);

    this.setCurrentPage(state.currentPage);

    this.setSort(state.sort);

    this.setColumnOrder(state.columnOrder);

    this.setHiddenColumns(state.hiddenColumns);

    this.setColumnWidths(state.columnsWidth);

    this.setFiltersBeforeBuild(state.filters);

    this.initColumns();
  },

  initColumns() {
    // Support columns as an Array or Collection
    this.options.columns = util.isArray(this.options.columns) ? new Collection(this.options.columns) : this.options.columns;

    this.columns = this.options.columns;
  },

  createStateModel(trackState, state) {
    if (trackState === true) {
      trackState = {};
    }

    if (!util.isEmpty(state)) {
      if (Array.isArray(state)) {
        state = this.convertStateToObject(state);
        trackState.array = true;
      }
      util.extend(trackState, state);
    }

    const model = new StateModel(trackState, {
      grid: this,
      array: trackState.array || false
    });

    if (util.isEmpty(state.filters)) {
      model.onFiltersLoaded();
    }

    return model;
  },

  loadDefaultSavedView() {
    if (util.isEmpty(this.options.savedViews) || !this.savedViewsCollection) {
      return this.loadStateObject();
    }
    const defaultModel = this.savedViewsCollection.getDefaultModel();

    this.loadSavedView(defaultModel);
  },

  loadSavedView(model) {
    const opts = {
      silent: true
    };

    this.options.state = model.toJSON() || {};

    if (this.stateModel) {
      this.stateModel.clear(opts).set(this.options.state, opts);
    }

    return this.loadStateObject();
  },

  fullGridUpdate(model) {
    this.usedSelectedView = true;
    this.close();
    this.loadSavedView(model);
    this.render();
    this.trigger('grid:fully:updated', this, model);
  },

  addListeners() {
    if (this.savedViews) {
      this.listenTo(this.savedViews, 'grid:use:savedView', this.fullGridUpdate);
    }

    if (this.filterView) {
      this.listenTo(this.filterView, 'filter', this.handleFilter);
    }

    if (this.multiColumnSortView) {
      this.listenTo(this, 'tableView:clientSidePagination:sort', () => {
        this.multiColumnSortView.trigger('turnOff');
      });
    }

    if (this.tableBody) {
      this.listenTo(this.tableBody, 'itemview:delete', this.handleDelete);
    }

    if (this.stateModel) {
      this.listenTo(this, {
        build() {
          this.listenTo(this.columns, {
            'change:condition': () => {
              this.stateModel.setHiddenColumns(this.columns.toJSON());
            },
            'change:width': () => {
              this.stateModel.setColumnWidths(this.columns.toJSON());
            }
          });
        },
        'state:filters:loaded': () => {
          this.stateModel.onFiltersLoaded();
        }
      });
    }
  },

  // added this function to check initial hidden columns in the columns properties.
  setDefaultHiddenColumns() {
    const columns = util.isArray(this.options.columns) ? new Collection(this.options.columns) : this.options.columns;

    const hiddenColumns = util.map(columns.filter((column) => {
      const condition = column.get('condition');

      return (util.isFunction(condition) && condition(column) === false) || condition === false;
    }), (column) => column.get('field'));

    if (!this.stateModel || this.stateModel.get('hiddenColumns').length) {
      return false;
    }

    // use silent to avoid triggering update on initial grid state
    this.stateModel.set('hiddenColumns', hiddenColumns || [], {
      silent: true
    });
  },

  /**
   * Filters the grid on the given filter(s).
   * @example grid.filter({
   *            filterField: 'someField',
   *            filterValue: 'someValue'
   *          }, {
   *            filterField: 'secondField',
   *            filterValue: 'secondValue'
   *          });
   */
  filter(...args) {
    const filters = Array.isArray(args[0]) ? args[0] : args;

    filters.forEach((filter) => {
      const { filterField, filterValue } = filter;
      this.collection.trigger(`grid:filter:${filterField}`, filterValue);
    });
  },

  processSelectedRowModels(models, selected) {
    if (selected) {
      this.selectedRowModels.add(models, {
        merge: true
      });
    } else {
      this.selectedRowModels.remove(models);
    }
  },

  restoreGridRowSelectedState() {
    const selectedModelIds = this.selectedRowModels.reduce((memo, model) => {
      // use the model.id property vs model.get('id')
      // as the id attribute may be aliased using Model.idAttribute
      if (!util.isNullOrUndefined(model.id)) {
        memo[model.id] = true;
      }

      return memo;
    }, {});

    if (this.masterCollection.length > 0) {
      this.masterCollection.each(model => {
        if (!util.isNullOrUndefined(model.id) && selectedModelIds[model.id]) {
          model.gridRowSelected = true;
        }
      });
    }
  },

  clearSelectedRows() {
    this.selectedRowModels = new Collection();
    this.$el.find('.grid-all-rows-selector').prop('checked', false);
  },

  enableStickySelectedRows({ serverSidePagination }) {
    if (serverSidePagination) {
      // for server side grids the collection changes with
      // each page so we restore the selected rows whenever they are showing
      this.listenTo(this.masterCollection, 'add reset sync', util.debounce(this.restoreGridRowSelectedState.bind(this)));
    } else {
      // for client side grids a reset or sync means we
      // have completely new data and need to wipe the stored selected rows
      this.listenTo(this.masterCollection, 'reset sync', this.clearSelectedRows);
    }

    this.selectedRowModels = new Collection();

    // handle single selecting/deselecting a row
    this.listenTo(this.appBus, 'grid:selectRow', rowSelectorCell => {
      if (this.cid === rowSelectorCell.gridCid) {
        this.processSelectedRowModels([rowSelectorCell.model], rowSelectorCell.model.gridRowSelected);
      }
    });

    this.listenTo(this.appBus, `grid:internal:selectAllRows:${this.cid}`, columnHeader => {
      const allRowsSelected = columnHeader.$el.find('.grid-all-rows-selector').is(':checked');
      this.processSelectedRowModels(this.gridCollection.models, allRowsSelected);
    });
  },

  // This method can be used outside on grid view instance in order not to re-render the whole grid
  // Full re-render is still an option, though
  processPreSelections(newIdsToSelect, rowIdColumn) {
    const alreadySelectedIds = [];
    const idsToDeselect = [];
    const preSelectedRowIdsObj = formatIdsToObject(newIdsToSelect);

    this.selectedRowModels.each(model => {
      if (preSelectedRowIdsObj[model.get(rowIdColumn)]) {
        alreadySelectedIds.push(model.get(rowIdColumn));
      } else {
        idsToDeselect.push(model.get(rowIdColumn));
      }
    });

    if (idsToDeselect && idsToDeselect.length) {
      this.deselectMatchedRows(idsToDeselect, rowIdColumn);
    }

    const idsToSelect = newIdsToSelect.filter(id => alreadySelectedIds.indexOf(id) < 0);

    if (idsToSelect && idsToSelect.length) {
      this.selectMatchedRows(idsToSelect, rowIdColumn);
    }

    this.updateSelections();
  },

  selectMatchedRows(idsToSelect, rowIdColumn) {
    const preSelectedRowIds = idsToSelect;

    if (!rowIdColumn || !preSelectedRowIds || !preSelectedRowIds.length) {
      return;
    }
    const preSelectedRowIdsObj = formatIdsToObject(preSelectedRowIds);
    const models = [];

    this.collection.each(model => {
      if (preSelectedRowIdsObj[model.get(rowIdColumn)]) {
        model.gridRowSelected = true;
        models.push(model);
      }
    });

    this.processSelectedRowModels(models, true);
  },

  deselectMatchedRows(idsToDeselect, rowIdColumn) {
    if (!rowIdColumn || !idsToDeselect || !idsToDeselect.length) {
      return;
    }

    const preSelectedRowIdsObj = formatIdsToObject(idsToDeselect);
    const models = [];

    this.collection.each(model => {
      if (preSelectedRowIdsObj[model.get(rowIdColumn)]) {
        model.gridRowSelected = false;
        models.push(model);
      }
    });

    this.processSelectedRowModels(models, false);
  },

  updateSelections() {
    const rowCidsToSelect = this.getSelectedRows();
    const rowCidsHasLength = rowCidsToSelect && rowCidsToSelect.length;

    if (!rowCidsHasLength) {
      this.footerView.bulkActionsView.model.set('bulkActionsDisabled', true);
      return;
    }

    const rowCids = this.collection.map(model => model.cid);
    const rowsToDeselect = rowCids.filter(id => rowCidsToSelect.indexOf(id) < 0);
    const doSelection = (rowCid, isSelected) => {
      const row = this.$el.find(`[data-model-cid=${rowCid}]`);
      const input = row.find('td:first-child .column-row-selector');
      row.toggleClass('selected', isSelected);
      input.prop('checked', isSelected);
    };

    if (rowsToDeselect && rowsToDeselect.length) {
      rowsToDeselect.forEach(rowToDeselect => doSelection(rowToDeselect, false));
    }

    rowCidsToSelect.forEach(rowToSelect => doSelection(rowToSelect, true));
    this.footerView.bulkActionsView.model.set('bulkActionsDisabled', false);
  }
});
